import type {
  ChangeEventHandler,
  CSSProperties,
  HTMLInputTypeAttribute,
} from 'react';
import React from 'react';
import styled from 'styled-components';
import Error from './Error';
import Label from './Label';

interface Props {
  /** Minimum number when used as a Number field */
  ariaLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string;
  height?: React.CSSProperties['height'];
  id?: string;
  inputStyle?: CSSProperties;
  label?: string;
  min?: number;
  name?: string;
  onBlur?: ChangeEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  readOnly?: boolean;
  touched?: boolean;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  value: string | number;
  width?: React.CSSProperties['width'];
}

const Input = styled.input<{
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
}>`
  display: block;
  text-decoration: none;
  margin-top: 4px;
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid
    ${({ disabled, error, required }) => {
      if (error || required) {
        return 'red';
      }
      if (disabled) {
        return 'lightgrey';
      }
      return '#d7dbec';
    }};
  cursor: text;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '32px'};
  color: ${({ disabled }) => (disabled ? 'grey' : 'inherit')};
  &:disabled {
    cursor: not-allowed;
  }
`;

const InputField = ({
  disabled,
  label,
  ariaLabel,
  name,
  type = 'text',
  id,
  placeholder,
  width,
  height,
  error,
  touched,
  onBlur,
  onChange,
  value,
  autoFocus,
  inputStyle,
  min,
  readOnly,
  required,
}: Props) => (
  <div style={{ display: 'flex', flexDirection: 'column', width }}>
    {label && <Label htmlFor={id || name}>{label}</Label>}
    <Input
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      disabled={disabled}
      error={Boolean(touched && error)}
      height={height}
      id={id || name}
      min={min}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      step={type === 'number' ? 'any' : undefined}
      style={inputStyle}
      type={type}
      value={value ?? ''}
      width={width}
      required={required}
    />
    <Error>{touched && error ? error : required ? 'Required' : null}</Error>
  </div>
);

export default InputField;
